import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Zap, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { subscriptionPlansApi } from '@/services/api';
import type { SubscriptionPlan } from '@/utils/subscriptionUtils';
import { getIntlLocale } from '@/utils/supportedLanguages';

interface SubscriptionPlansProps {
  agentId: number;
  agentName?: string;
  onPlanSelect?: (planId: string) => void;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  agentId,
  agentName,
  onPlanSelect,
}) => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedPlans =
          await subscriptionPlansApi.fetchSubscriptionPlans();
        setPlans(fetchedPlans);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : t('subscription.plans.fetch_error_fallback')
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [t]);

  const isRecommendedPlan = (plan: SubscriptionPlan) => {
    const name = plan.name.trim().toLowerCase();
    return name === 'after hours plus' || name === 'pro';
  };

  const getTierDescription = (plan: SubscriptionPlan) => {
    const name = plan.name.trim().toLowerCase();
    if (plan.crm_included || name === 'business') {
      return t('subscription.plans.tier_description.enterprise');
    }
    if (name === 'after hours plus' || name === 'pro') {
      return t('subscription.plans.tier_description.growing');
    }
    return t('subscription.plans.tier_description.starter');
  };

  const generateFeatures = (plan: SubscriptionPlan) => {
    const features = [
      t('subscription.plans.feature.after_hours_answering'),
      t('subscription.plans.feature.caller_details'),
      t('subscription.plans.feature.business_information'),
      t('subscription.plans.feature.summary_transcript'),
      t('subscription.plans.feature.notifications'),
      t('subscription.plans.feature.phone_number'),
    ];

    if (plan.crm_included) {
      features.push(t('subscription.plans.feature.crm_workflow'));
    }

    return features;
  };

  const formatMoney = (amount: number, currency: string) =>
    new Intl.NumberFormat(getIntlLocale(i18n.resolvedLanguage), {
      style: 'currency',
      currency,
    }).format(amount);

  const formatPeriod = (period: SubscriptionPlan['billing_period']) =>
    t(`subscription.plans.period.${period}`, period);

  const formatOverageRate = (plan: SubscriptionPlan) =>
    t('subscription.plans.overage_rate', {
      rate: formatMoney(plan.overage_price_per_minute, plan.currency),
      defaultValue: '{{rate}} / min',
    });

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setDialogOpen(true);
  };

  const handleRequestSubscription = async () => {
    if (!selectedPlan) return;

    try {
      setRequestLoading(true);
      await subscriptionPlansApi.createSubscription({
        agentId,
        subscriptionPlanId: selectedPlan.id,
      });

      // Success - close dialog and call parent callback
      onPlanSelect?.(selectedPlan.id.toString());
      setDialogOpen(false);
      setSelectedPlan(null);
    } catch (err) {
      console.error('Failed to create subscription request:', err);
      // You could add a toast notification here or handle the error differently
      // For now, we'll just log it and keep the dialog open
    } finally {
      setRequestLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedPlan(null);
  };

  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center space-x-2">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>
              {t('subscription.plans.loading', 'Loading subscription plans...')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-6xl mx-auto p-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4 text-red-600">
            {t('subscription.plans.error', 'Error Loading Plans')}
          </h1>
          <p className="text-lg text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            {t('common.retry', 'Retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4">
          {t('subscription.plans.title', 'Choose Your Subscription Plan')}
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          {t(
            'subscription.plans.description',
            'Select the perfect plan for your agent to unlock advanced features and grow your business.'
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map(plan => {
          const isRecommended = isRecommendedPlan(plan);
          const features = generateFeatures(plan);

          return (
            <Card
              key={plan.id}
              className={`relative flex flex-col transition-all duration-200 ${
                isRecommended ? 'scale-105 border-primary' : 'border-border'
              }`}
            >
              {isRecommended && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-green-500 px-4 py-1 text-primary-foreground">
                    <Zap className="w-3 h-3 mr-1" />
                    {t('subscription.plans.recommended', 'Recommended')}
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>{getTierDescription(plan)}</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold">
                    {formatMoney(plan.price, plan.currency)}
                  </span>
                  <span className="text-muted-foreground ml-1">
                    /{formatPeriod(plan.billing_period)}
                  </span>
                </div>
              </CardHeader>

              <CardContent className="space-y-6 flex-grow flex flex-col">
                <div className="space-y-2">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    {t('subscription.plans.terms', 'Plan terms')}
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>
                        {t(
                          'subscription.plans.included_minutes',
                          'Included minutes'
                        )}
                        :
                      </span>
                      <span className="font-medium">
                        {plan.included_minutes.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('subscription.plans.overage', 'Overage')}:</span>
                      <span className="font-medium">
                        {formatOverageRate(plan)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 flex-grow">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    {t('subscription.plans.features', 'Features')}
                  </h4>
                  <ul className="space-y-2">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  onClick={() => handlePlanSelect(plan)}
                  className={`w-full mt-auto ${
                    isRecommended
                      ? 'bg-primary hover:bg-primary/90'
                      : 'bg-secondary hover:bg-secondary/90'
                  }`}
                  size="lg"
                >
                  {t('subscription.plans.select', 'Select Plan')}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="mt-8 text-center">
        <p className="text-sm text-muted-foreground">
          {t(
            'subscription.plans.disclaimer',
            'Plan terms are fixed for the current billing period; template changes apply at renewal.'
          )}
        </p>
      </div>

      {/* Plan Selection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {selectedPlan?.name}{' '}
              {t('subscription.plans.dialog.title', 'Plan Details')}
            </DialogTitle>
            <DialogDescription>
              {agentName && (
                <div className="mb-2">
                  {t('subscription.plans.dialog.agent', 'For Agent')}:{' '}
                  <span className="font-semibold">{agentName}</span>
                </div>
              )}
              {t(
                'subscription.plans.dialog.description',
                'Review your selected plan details before requesting subscription.'
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedPlan && (
            <div className="space-y-6">
              {/* Plan Overview */}
              <div className="bg-muted/50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold">
                      {selectedPlan.name}
                    </h3>
                    <p className="text-muted-foreground">
                      {getTierDescription(selectedPlan)}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">
                      {formatMoney(selectedPlan.price, selectedPlan.currency)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      /{formatPeriod(selectedPlan.billing_period)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan terms */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">
                  {t('subscription.plans.terms', 'Plan terms')}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex justify-between p-3 bg-muted/30 rounded">
                    <span className="text-sm text-muted-foreground">
                      {t(
                        'subscription.plans.included_minutes',
                        'Included minutes'
                      )}
                      :
                    </span>
                    <span className="font-medium">
                      {selectedPlan.included_minutes.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between p-3 bg-muted/30 rounded">
                    <span className="text-sm text-muted-foreground">
                      {t('subscription.plans.overage', 'Overage')}:
                    </span>
                    <span className="font-medium">
                      {formatOverageRate(selectedPlan)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">
                  {t('subscription.plans.features', 'Features')}
                </h4>
                <ul className="space-y-2">
                  {generateFeatures(selectedPlan).map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={handleDialogClose}
                  disabled={requestLoading}
                >
                  {t('common.cancel.translation')}
                </Button>
                <Button
                  onClick={handleRequestSubscription}
                  size="lg"
                  disabled={requestLoading}
                >
                  {requestLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('subscription.plans.requesting', 'Requesting...')}
                    </>
                  ) : (
                    t('subscription.plans.request', 'Request Subscription')
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPlans;
